import { PROMOTION_SEGMENT_CODES, PROMOTIONS_PAGE_SIZE } from '../configs/service'
import { type IsoDate, SortOrder } from '../models/common'
import type { PromotionAttributeFilter } from '../models/productFilter'
import type {
  DealEndDateFilter,
  PromotionSortAxis,
  PromotionSortDirection,
  PromotionsSearchQuery,
} from '../models/query'

import type { PromotionAttributeFilterDto, PromotionsSearchRequestDto } from './dto'

/** Pages are 1-based everywhere — in the URL, in the query and here. */
const FIRST_PAGE = 1

/**
 * The window the list is about.
 *
 * With nothing chosen it is "has not ended": `endsAfter = today`, which is what
 * FR-9 rests on — offers that have not started yet arrive and get drawn dimmed
 * with an "Upcoming" badge, where `activeOn` would have dropped them before the
 * client ever saw them.
 *
 * A date in the control **replaces** that default rather than narrowing it. It
 * is the user saying which window they want, and they are allowed to ask for one
 * that has already closed: "Before 30 Sep 2025" is how you look at deals that
 * have ended. Flooring it at today would answer a question nobody asked and, for
 * "After", would send a different date than the field shows.
 */
const toWindow = (referenceDate: IsoDate, dealEndDate?: DealEndDateFilter) => {
  if (!dealEndDate) {
    return { endsAfter: referenceDate }
  }

  const { operator, date } = dealEndDate

  if (operator === 'before') {
    return { endsBefore: date }
  }

  return operator === 'on' ? { endsOn: date } : { endsAfter: date }
}

const trim = (value?: string) => {
  const trimmed = value?.trim()

  return trimmed ? trimmed : undefined
}

/**
 * The last gate before the wire: one object per filter, carrying its `kind` and
 * the one field that kind names.
 *
 * `kind` is what the service parses the object by, and the fields of the other
 * two forms are stripped on the way in — so a filter that named both values and
 * bounds would lose one of them without a word. Naming nothing at all is a 400
 * too, not a "no filter": an unselected group disappears from the body, and an
 * empty list is refused the same way, which is why clearing the last tick drops
 * the object rather than emptying it.
 */
const toAttributeDto = (
  attribute: PromotionAttributeFilter
): PromotionAttributeFilterDto | null => {
  const filterKey = attribute.filterKey.trim()

  if (!filterKey) {
    return null
  }

  switch (attribute.kind) {
    case 'values':
      return attribute.values.length > 0
        ? { filterKey, kind: 'values', values: attribute.values }
        : null
    case 'groups':
      return attribute.groups.length > 0
        ? { filterKey, kind: 'groups', groups: attribute.groups }
        : null
    default:
      // A range naming neither side says nothing, and the service says so with a
      // 400 rather than with the whole list.
      return attribute.min !== undefined || attribute.max !== undefined
        ? {
            filterKey,
            kind: 'range',
            ...(attribute.min !== undefined ? { min: attribute.min } : {}),
            ...(attribute.max !== undefined ? { max: attribute.max } : {}),
          }
        : null
  }
}

const toAttributes = (
  attributes: readonly PromotionAttributeFilter[] = []
): readonly PromotionAttributeFilterDto[] =>
  attributes
    .map(toAttributeDto)
    .filter((attribute): attribute is PromotionAttributeFilterDto => attribute !== null)

/**
 * The "Sorted by" dropdown as the two fields the service takes: the axis and its
 * direction.
 *
 * Stock maps to neither. The service resolves stock a page at a time and has no
 * axis for it, so ordering the whole result by it would mean pulling the whole
 * result — a step nobody has taken. Sending nothing leaves the service's own
 * default in place (`end_date` ascending) instead of a value it would refuse.
 */
const SORT_AXES: Readonly<Record<SortOrder, PromotionSortAxis | undefined>> = {
  [SortOrder.StockAsc]: undefined,
  [SortOrder.StockDesc]: undefined,
  [SortOrder.StandardDiscountAsc]: 'standard_discount',
  [SortOrder.StandardDiscountDesc]: 'standard_discount',
  [SortOrder.AdditionalDiscountAsc]: 'additional_discount',
  [SortOrder.AdditionalDiscountDesc]: 'additional_discount',
  [SortOrder.DealEndDateAsc]: 'end_date',
  [SortOrder.DealEndDateDesc]: 'end_date',
}

const DESCENDING: readonly SortOrder[] = [
  SortOrder.StockDesc,
  SortOrder.StandardDiscountDesc,
  SortOrder.AdditionalDiscountDesc,
  SortOrder.DealEndDateDesc,
]

const toSortFields = (sort: SortOrder) => {
  const sortBy = SORT_AXES[sort]

  if (!sortBy) {
    return {}
  }

  const sortDir: PromotionSortDirection = DESCENDING.includes(sort) ? 'desc' : 'asc'

  return { sortBy, sortDir }
}

/**
 * Translates the search query into the search body. Client-side concerns
 * (`search`, `endUserPromosOnly`) are kept out of the query type itself: the
 * service would silently drop them (`whitelist: true`), which is worse than not
 * sending them at all.
 *
 * Sorting is the service's job for the same reason the page cut is: an order
 * applied to one loaded page is not the order of the result. The dropdown's own
 * code is translated into the contract's two fields here — see `toSortFields`
 * for the one entry that maps to neither.
 *
 * Every toolbar field goes over as a single value. The service rejects an array
 * there outright, so the one place that could have wrapped them — this function —
 * is also the one place that must not.
 *
 * `referenceDate` is passed in rather than read here, and that is the point of
 * the parameter: the same date has to floor the window on the way out and stamp
 * `PromotionsDataset.activeOn` on the way back, or a search started a second
 * before midnight would be judged against a day the service never saw. The data
 * source reads it once and hands it to both.
 */
export const buildPromotionsSearchRequest = (
  query: PromotionsSearchQuery,
  referenceDate: IsoDate
): PromotionsSearchRequestDto => {
  const dealId = trim(query.deal)
  const productLine = trim(query.productLine)
  const mccCode = trim(query.mcc)
  const reseller = trim(query.reseller)
  const attributes = toAttributes(query.attributes)

  return {
    segment: PROMOTION_SEGMENT_CODES[query.segment],
    // The only country axis of the search: content, specifications and
    // filterable values are all read for the countries of the found offers.
    // An empty list means "no country filter", not "no countries".
    countries: query.countries,
    ...(dealId ? { dealId } : {}),
    ...(productLine ? { productLine } : {}),
    ...(mccCode ? { mccCode } : {}),
    ...(query.promotionType ? { promotionType: query.promotionType } : {}),
    ...toWindow(referenceDate, query.dealEndDate),
    ...(reseller ? { reseller } : {}),
    ...(attributes.length > 0 ? { attributes } : {}),
    // `product` is the service default, so only the flat cut is worth a field.
    ...(query.groupBy === 'sku' ? { groupBy: query.groupBy } : {}),
    ...toSortFields(query.sort),
    // `skip` and `take` count list rows, and `groupBy` decides what a row is —
    // so the page cut moves with the grouping toggle, which is why both live on
    // the search and not on the view projector.
    skip: (Math.max(query.page ?? FIRST_PAGE, FIRST_PAGE) - FIRST_PAGE) * PROMOTIONS_PAGE_SIZE,
    take: PROMOTIONS_PAGE_SIZE,
  }
}
